import React, { useState } from 'react';
import { Typography } from 'antd';
import { RightOutlined, AppstoreOutlined } from '@ant-design/icons';
import { formatPrice } from '../../utils/formatPrice';
import { useHomeStrings } from '../../hooks/useHomeStrings';
import type { HomeService } from '../../model/types';

interface Props {
  service: HomeService;
  showPrice: boolean;
  onClick?: () => void;
}

export const ServiceItem: React.FC<Props> = React.memo(({ service, showPrice, onClick }) => {
  const STRINGS = useHomeStrings();
  const [pressed, setPressed] = useState(false);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={
        onClick
          ? `${service.name}, ${STRINGS.DURATION_MINUTES(service.durationMinutes)}`
          : undefined
      }
      onClick={onClick}
      onKeyDown={handleKeyDown}
      onMouseDown={() => onClick && setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      onTouchStart={() => onClick && setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      onTouchCancel={() => setPressed(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '11px 12px',
        borderRadius: 12,
        background: pressed
          ? 'var(--bl-divider, #ebebeb)'
          : 'var(--bl-surface)',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'background 0.12s',
        outline: 'none',
        minHeight: 48,
      }}
    >
      {/* Icon circle */}
      <div
        style={{
          width: 34,
          height: 34,
          borderRadius: 8,
          flexShrink: 0,
          background: 'var(--bl-divider)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 14,
          color: 'var(--bl-primary)',
        }}
      >
        <AppstoreOutlined />
      </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <Typography.Text
          style={{
            display: 'block',
            fontWeight: 600,
            fontSize: 15,
            color: 'var(--bl-text)',
          }}
        >
          {service.name}
        </Typography.Text>
        <Typography.Text style={{ fontSize: 13, color: 'var(--bl-text-secondary)' }}>
          {STRINGS.DURATION_MINUTES(service.durationMinutes)}
        </Typography.Text>
      </div>

      {/* Price + arrow */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        {showPrice && service.price !== null && (
          <div style={{ textAlign: 'right' }}>
            <Typography.Text
              style={{ fontWeight: 700, fontSize: 15, color: 'var(--bl-text)' }}
            >
              {formatPrice(service.price)}
            </Typography.Text>
          </div>
        )}
        {onClick && (
          <RightOutlined style={{ fontSize: 11, color: 'var(--bl-text-secondary)' }} />
        )}
      </div>
    </div>
  );
});

ServiceItem.displayName = 'ServiceItem';
